import React, { useState } from 'react';
import { 
  Box, 
  Alert,
  Tooltip
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel';
import { ACL, ACLAuthorization, AccessMode } from '@/api/bol';
import DataGridContainer from '@/components/common/DataGridContainer';
import { StandardActions } from '@/components';
import { 
  createACLAuthorizationColumns, 
  defaultACLAuthorizationColumnVisibilityModel 
} from './ACLAuthorizationColumns';
import ACLAuthorizationCreateDialog from './ACLAuthorizationCreateDialog';
import ACLAuthorizationEditDialog from './ACLAuthorizationEditDialog';
import ACLAuthorizationDeleteDialog from './ACLAuthorizationDeleteDialog';
import { useACL, usePermissions, getActionTooltip } from '@/hooks';
import type { LogisticsObjectEntity } from '@/types/logisticsObject';

interface ACLAuthorizationsListProps {
  logisticsObjectType: LogisticsObjectEntity;
  objectId: string;
  acl: ACL | null;
  onACLUpdated: () => Promise<void>;
  onAuthorizationSelected?: (authorization: ACLAuthorization | null) => void;
  selectedAuthorizationId?: string | null;
}

const ACLAuthorizationsList: React.FC<ACLAuthorizationsListProps> = ({
  logisticsObjectType,
  objectId,
  acl,
  onACLUpdated,
  onAuthorizationSelected,
  selectedAuthorizationId
}) => {
  
  const { can } = usePermissions();
  const { upsertAuthorization, deleteAuthorization } = useACL(logisticsObjectType, objectId);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAuthorization, setSelectedAuthorization] = useState<ACLAuthorization | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(
    defaultACLAuthorizationColumnVisibilityModel
  );

  const authorizations = React.useMemo(() => acl?.authorizations || [], [acl]);

  const controlMemberIdentifiers = React.useMemo(() => {
    const identifiers = authorizations
      .filter((authorization) => authorization.mode === 'CONTROL')
      .map((authorization) => authorization.memberIdentifier);
    return Array.from(new Set(identifiers));
  }, [authorizations]);

  // Sync external selection with local state
  React.useEffect(() => {
    if (selectedAuthorizationId !== undefined && selectedAuthorizationId !== selectedRowId) {
      setSelectedRowId(selectedAuthorizationId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAuthorizationId]);

  const handleAddClick = () => {
    setCreateDialogOpen(true);
  };

  const handleEditClick = (authorization: ACLAuthorization) => {
    setSelectedAuthorization(authorization);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (authorization: ACLAuthorization) => {
    setSelectedAuthorization(authorization);
    setDeleteDialogOpen(true);
  };

  const handleSubmitAuthorization = async (memberIdentifier: string, mode: AccessMode, authorizedBy: string) => {
    await upsertAuthorization(memberIdentifier, mode, authorizedBy, acl?.revision);
    await onACLUpdated();
  };

  const handleDeleteAuthorization = async (authorizedBy: string) => {
    if (selectedAuthorization) {
      await deleteAuthorization(selectedAuthorization.memberIdentifier, authorizedBy, acl?.revision);
      await onACLUpdated();
    }
  };

  const handleRefresh = async () => {
    await onACLUpdated();
  };

  const handleRowSelectionChange = (selection: GridRowSelectionModel) => {
    const newRowId = selection.length > 0 ? String(selection[0]) : null;
    setSelectedRowId(newRowId);
    const selectedAuth = newRowId
      ? authorizations.find(a => a.memberIdentifier === newRowId) || null
      : null;
    onAuthorizationSelected?.(selectedAuth);
  };

  const columns = createACLAuthorizationColumns({
    onEdit: handleEditClick,
    onDelete: handleDeleteClick,
    canEdit: can.updateLogisticsObject,
    canDelete: can.updateLogisticsObject,
    editTooltip: getActionTooltip('updateLogisticsObject'),
    deleteTooltip: getActionTooltip('updateLogisticsObject')
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {authorizations.length === 0 ? (
        <Alert severity="info">
          No ACL authorizations found. Add an authorization to grant access to this logistics object.
        </Alert>
      ) : (
        <DataGridContainer
          rows={authorizations}
          columns={columns}
          loading={false}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          getRowId={(row: ACLAuthorization) => row.memberIdentifier}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(model: Record<string, boolean>) => 
            setColumnVisibilityModel(model)
          }
          columnOrderStorageKey={`${logisticsObjectType}-acl-columns`}
          hideFooterPagination
          sx={{ flex: 1 }}
          actionButtons={
            <>
              <Tooltip title={!can.updateLogisticsObject ? getActionTooltip('updateLogisticsObject') : ''}>
                <span>
                  <StandardActions
                    onAdd={handleAddClick}
                    addButtonText="Add Authorization"
                    addButtonIcon={<Add />}
                    disabled={!can.updateLogisticsObject}
                  />
                </span>
              </Tooltip>
              <StandardActions
                onRefresh={handleRefresh}
                refreshTooltip="Refresh ACL"
              />
            </>
          }
        />
      )}

      {/* Create Dialog */}
      <ACLAuthorizationCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleSubmitAuthorization}
        controlMemberIdentifiers={controlMemberIdentifiers}
      />

      {/* Edit Dialog */}
      {selectedAuthorization && (
        <ACLAuthorizationEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSubmit={handleSubmitAuthorization}
          authorization={selectedAuthorization}
        />
      )}

      {/* Delete Dialog */}
      <ACLAuthorizationDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteAuthorization}
        authorization={selectedAuthorization}
        controlMemberIdentifiers={controlMemberIdentifiers}
      />
    </Box>
  );
};

export default ACLAuthorizationsList;
